// The selected group's right-side detail. The real GET …/group/{id} route
// (Task #5309, merged) serves the ids-and-score-only shape as the overview
// list — group_id, member_project_ids, score — plus a real lifecycle_status
// read (Task #5311, live on dev ahead of merging to `main`; see
// types/whitespace.ts's note on OverlapGroup.lifecycle_status). Design
// §6.3's members-with-differentiators, inventor identity and funding chips
// are still NOT in that payload — this view renders those as an explicit
// "not available yet" gap rather than fabricating them, and shows the real
// per-member fact it does have (name + document count, from the portfolio
// project list this page already fetches). The lifecycle status control and
// the fund-once workbench (Story #5305 Stream 14b, Task #5318) ARE live —
// see OverlapLifecycleControl.tsx / OverlapWorkbench.tsx.
import { useQuery } from '@tanstack/react-query'
import { overlapRelationshipHeadline, overlapStrongestPairAnchor } from '../../lib/whitespace'
import { listAggregatorTeamMembers } from '../../services/api'
import OverlapMiniMap from './OverlapMiniMap'
import OverlapLifecycleControl from './OverlapLifecycleControl'
import OverlapWorkbench from './OverlapWorkbench'
import EvidenceTable from './EvidenceTable'
import HelpPopover from '../help/HelpPopover'
import type { AggregatorProject } from '../../types'
import type { OverlapGroupDetailResponse, PortfolioMapLayout } from '../../types/whitespace'

interface GroupDetailProps {
  group: OverlapGroupDetailResponse
  projectsById: Map<string, AggregatorProject>
  layout: PortfolioMapLayout | null
  aggregatorId: string | null
}

export default function GroupDetail({ group, projectsById, layout, aggregatorId }: GroupDetailProps) {
  const projectNames = new Map(group.member_project_ids.map((id) => [id, projectsById.get(id)?.name ?? id]))
  const headline = overlapRelationshipHeadline(group.score, group.member_project_ids.length)

  // Gated on the caller's own role from the API, not on whether the roster
  // the client already has happens to contain an admin/billing_owner row —
  // same security precedent TeamManagement.tsx documents for this exact
  // query, and the same query key, so a visit to Team Management costs this
  // view nothing extra.
  const { data: teamData } = useQuery({
    queryKey: ['aggregator-team-members', aggregatorId],
    queryFn: listAggregatorTeamMembers,
  })
  const callerRole = teamData?.caller_role ?? null

  return (
    <div data-testid="overlap-group-detail" data-tour="overlap-group-detail" className="flex flex-col gap-2.5">
      <div className="card p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <div className="text-[15px] font-extrabold text-iflow-900">{headline}</div>
              <HelpPopover topic="pf-overlap-actions" size={13} />
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              {overlapStrongestPairAnchor(group.score)} · computed over everything you're entitled to see
            </div>
          </div>
          <OverlapLifecycleControl
            groupId={group.group_id}
            aggregatorId={aggregatorId}
            status={group.lifecycle_status ?? 'new'}
            callerRole={callerRole}
            onStatusChanged={() => { /* query invalidation (see the control's own onSuccess) refreshes `group` */ }}
          />
        </div>

        <div className="flex gap-4 mt-3">
          <div style={{ flex: 1.15 }}>
            {group.member_project_ids.map((projectId) => {
              const project = projectsById.get(projectId)
              return (
                <div key={projectId} className="flex items-start gap-2 py-1.5 border-b border-dashed border-gray-200 text-[12px] last:border-none">
                  <div>
                    <b className="text-iflow-900">{project?.name ?? projectId}</b>
                    {project?.entity_name && <> · {project.entity_name}</>}
                    {project && <> · {project.document_count} docs</>}
                    <br />
                    <span className="text-gray-500 italic">Differentiator — not available yet</span>
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ flex: 0.85 }}>
            <OverlapMiniMap layout={layout} memberProjectIds={group.member_project_ids} projectNames={projectNames} />
          </div>
        </div>
      </div>

      <div className="card p-3">
        <EvidenceTable rows={[]} />
        <p className="text-[10.5px] text-gray-400 italic mt-1" data-testid="overlap-evidence-unavailable">
          Document-pair evidence isn't available yet — this overlap is currently based on similarity scores between
          projects. The specific document pairs behind it will appear in a later update.
        </p>
      </div>

      <OverlapWorkbench
        groupId={group.group_id}
        aggregatorId={aggregatorId}
        memberCount={group.member_project_ids.length}
        callerRole={callerRole}
      />
    </div>
  )
}
